import React, { useState } from 'react';
import { ArrowPathIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

import Modal, { ModalActionButton, ModalCancelButton } from './Modal';
import useJobProgressStore from '../stores/jobProgressStore';
import { getAdapter } from '../lib/jobAdapters';
import { isWarning } from '../lib/jobStatus';

// Static accent → bar classes (Tailwind can't see dynamic `bg-${x}-500`).
const ACCENT_BAR = {
  amber: 'bg-amber-500 dark:bg-amber-400',
  sage: 'bg-sage-500 dark:bg-sage-400',
  rose: 'bg-rose-500 dark:bg-rose-400',
  indigo: 'bg-indigo-500 dark:bg-indigo-400',
};

/**
 * The expanded job-progress modal (one focused job at a time). Renders a live
 * progress bar (indeterminate when percent is unknown), the current step, counts,
 * and error details. "Run in background" minimizes to a toast; on terminal the
 * footer becomes Close. A paused (shop-reset needs-input) job shows a resolve
 * affordance pointing at the Shop Reset tab.
 */
const JobProgressModal = ({ job }) => {
  const minimize = useJobProgressStore(s => s.minimize);
  const dismiss = useJobProgressStore(s => s.dismiss);
  const [showErrors, setShowErrors] = useState(false);

  if (!job) return null;

  const adapter = getAdapter(job.kind);
  const n = adapter.normalize(job.raw, job.meta);
  const { statusKind, phaseLabel, currentStep, percent, counts } = n;
  const terminal = statusKind === 'terminal-ok' || statusKind === 'terminal-err';
  const warn = isWarning(job.raw?.status);
  const barClass = ACCENT_BAR[job.accent] || ACCENT_BAR.indigo;
  const errors = Array.isArray(job.raw?.error_details) ? job.raw.error_details : [];

  const titleIcon =
    statusKind === 'terminal-err' ? (
      <XCircleIcon className="w-5 h-5 text-red-500" />
    ) : statusKind === 'terminal-ok' ? (
      warn ? (
        <ExclamationTriangleIcon className="w-5 h-5 text-amber-500" />
      ) : (
        <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
      )
    ) : (
      <ArrowPathIcon className="w-5 h-5 text-gray-500 animate-spin" />
    );

  const handleClose = () => (terminal ? dismiss(job.id) : minimize(job.id));

  const footer = terminal ? (
    <ModalActionButton onClick={() => dismiss(job.id)} color={statusKind === 'terminal-err' ? 'red' : 'emerald'}>
      Done
    </ModalActionButton>
  ) : (
    <ModalCancelButton onClick={() => minimize(job.id)}>Run in background</ModalCancelButton>
  );

  return (
    <Modal
      isOpen
      onClose={handleClose}
      closeOnBackdrop={false}
      title={job.label}
      titleIcon={titleIcon}
      subtitle={phaseLabel}
      footer={footer}
      maxWidth="max-w-lg"
    >
      {statusKind === 'paused' ? (
        <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-300">
          This job is paused and needs your input{currentStep ? `: ${currentStep}` : ''}. Open the{' '}
          <strong>Shop Reset</strong> tab to resolve the listing match, then it will continue automatically.
        </div>
      ) : (
        <>
          {/* Progress bar (indeterminate when percent is null) */}
          <div
            className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden"
            role="progressbar"
            aria-valuenow={percent ?? undefined}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${job.label} progress`}
          >
            {percent === null ? (
              <div className={`${barClass} h-2.5 rounded-full w-full animate-pulse`} />
            ) : (
              <div
                className={`${barClass} h-2.5 rounded-full transition-all duration-300`}
                style={{ width: `${percent}%` }}
              />
            )}
          </div>

          {!terminal && currentStep && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 truncate" title={currentStep}>
              {currentStep}
            </p>
          )}

          {counts && (counts.total > 0 || terminal) && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {counts.succeeded} ok
              {counts.failed > 0 && <span className="text-red-500"> · {counts.failed} failed</span>}
              {counts.total > 0 && ` · ${counts.completed}/${counts.total}`}
            </p>
          )}

          {job.raw?.message && terminal && (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">{job.raw.message}</p>
          )}

          {errors.length > 0 && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowErrors(v => !v)}
                className="text-xs text-red-600 dark:text-red-400 underline"
              >
                {showErrors ? 'Hide' : 'Show'} {errors.length} issue(s)
              </button>
              {showErrors && (
                <ul className="mt-1 max-h-40 overflow-y-auto text-xs text-red-600 dark:text-red-400 list-disc list-inside space-y-1">
                  {errors.map((e, i) => (
                    <li key={i} className="break-words">
                      {typeof e === 'string' ? e : e?.message || JSON.stringify(e)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default JobProgressModal;
